import HomeLayout from "./layout/HomeLayout.tsx";
import HomeDashboard from "./dashboard/Home.tsx";
import MyLibrary from "./my-library/MyLibrary.tsx";
import { redeemCodesAction } from "./actions/redeem/redeem.ts";
import InsetHomeError from "./InsetHomeError.tsx";
import Expired from "./expired/Expired.tsx";
import Favorites from "./favorites/Favorites.tsx";
import { revealCode } from "./actions/courseware/courseware.ts";
import { searchRoutes } from "./search/searchRoutes.tsx";
import { dashboardLoader } from "./dashboard/dashboardLoader.ts";
import {
  libraryLoader,
  loadRecentExploreAssets,
} from "./my-library/libraryLoader.ts";
import { expiredLoader } from "./expired/expiredLoader.ts";
import {
  getDynamicLink,
  updateUser,
  updateUserPreference,
} from "./actions/user/user.ts";
import { returnAction } from "./actions/explore/return/return.ts";
import { HomeRouteHandle } from "./homeRouteHandle.ts";
import { activateAction } from "./actions/explore/activate/activate.ts";
import { homeRouteId, loadHomeData } from "./homeContext.ts";
import { redeemLoader } from "./redeem/redeemLoader.ts";
import RedeemMain from "./redeem/RedeemMain.tsx";
import { favorites } from "./actions/favorites/favorites.ts";
import { favoritesLoader } from "./favorites/favorites.ts";
import { subscribeLoader } from "./subscribe/subscribeLoader.ts";
import SubscribeMain from "./subscribe/SubscribeMain.tsx";
import Admin from "./admin/Admin.tsx";
import Groups from "./groups/Groups.tsx";
import exploreRedirector from "./common/explore/exploreRedirect.ts";
import myCoursesRoutes from "./my-courses/myCoursesRoutes.tsx";
import { adoptionsRoutes } from "./adoptions/adoptionsRoutes.ts";
import { booksRouteLoader } from "./books/booksRouteLoader.ts";
import AssetActivator from "./activate/AssetActivator.tsx";
import { activateLoader } from "./activate/activateLoader.ts";
import { ShouldRevalidateFunctionArgs } from "react-router-dom";
import { updateLibraryAction } from "./actions/update-library/updateLibrary.ts";
import { expiredDataLoader } from "./data/expired/expiredDataLoader.ts";
import { recommendationsDataLoader } from "./data/recommendations/recommendationsDataLoader.ts";
import {
  promotionActivateAction,
  promotionReturnAction,
} from "./actions/explore-promotion/promotions.ts";
import { exploreRoutes } from "./explore-switcher/exploreRoutes.tsx";
import { getInstructorCoursesLoader } from "./manage-engagement-score/manageEngagementScoreLoader.ts";
import { recommendationsRoutes } from "./recommendations/recommendationsRoutes.ts";
import { myCartRoutes } from "./my-cart/myCartRoutes.ts";
import OrderHistory from "./order-history/OrderHistory.tsx";
import { productRoutes } from "./product/productRoutes.ts";
import { facultyProductRoutes } from "./faculty-products/facultyProductRoutes.ts";
import { once } from "../../common/utils/function.ts";
import { courseRedirectLoader } from "./course-redirect/courseRedirectLoader.ts";
import CourseRedirect from "./course-redirect/CourseRedirect.tsx";
import { activateFreeBookAction } from "./free-books/activate.ts";
import FreeBookActivator from "./free-books/FreeBookActivator.tsx";
import { tableOfContentsDataLoader } from "./data/table-of-contents/tableOfContentsDataLoader.ts";

const loadHomeDataOnce = once(() => {
  return loadHomeData();
});

export const homeRoutes = {
  id: homeRouteId,
  // We were observing some strange issues with shouldRevalidate not being called
  // consistently and could not determine the cause. Rather than rely on that,
  // just cache the loader result.
  loader: async () => {
    try {
      return await loadHomeDataOnce();
    } catch (error) {
      if (error instanceof Response) {
        // A Response can only have its body read once. Clone so it can be re-read
        // with each loader function invocation
        throw error.clone();
      } else {
        throw error;
      }
    }
  },
  // But you still need shouldRevalidate or there can be weird timing things
  // during initial load redirects
  shouldRevalidate: () => false,
  children: [
    {
      element: <HomeLayout />,
      children: [
        {
          errorElement: <InsetHomeError />,
          children: [
            {
              path: "/dashboard",
              element: <HomeDashboard />,
              loader: dashboardLoader,
            },
            { ...myCoursesRoutes },
            adoptionsRoutes,
            { ...exploreRoutes },
            {
              path: "/classes",
              element: <Groups />,
            },
            {
              path: "/classes/*",
              loader: exploreRedirector,
            },
            {
              path: "/admin",
              element: <Admin />,
            },
            {
              path: "/admin/*",
              loader: exploreRedirector,
            },
            {
              path: "/my-library",
              element: <MyLibrary />,
              // Does not have its own loader because it is preferable for all pages
              // to load in the exact same way
              children: [
                { path: "/my-library/assets/:page", loader: libraryLoader },
                {
                  path: "/my-library/recent-explore-assets",
                  loader: loadRecentExploreAssets,
                },
              ],
            },
            {
              path: "/expired",
              element: <Expired />,
              // Does not have its own loader because it is preferable for all pages
              // to load in the exact same way
              children: [
                { path: "/expired/assets/:page", loader: expiredLoader },
              ],
            },
            {
              path: "/favorites",
              element: <Favorites />,
              loader: favoritesLoader,
              shouldRevalidate: (args: ShouldRevalidateFunctionArgs) => {
                // if on favorites, clicking the same link to favorites (i.e. stay on favorites)
                // caused the loader to run again, unnecessary and interfered with a re-focus rect
                // but we do want revalidation for say a DELETE etc
                return !!args.formMethod;
              },
            },
            recommendationsRoutes,
            myCartRoutes,
            productRoutes,
            facultyProductRoutes,
            ...searchRoutes,
            {
              path: "/manage-engagement-score",
              async lazy() {
                const { manageEngagementScore } = await import(
                  "./manage-engagement-score/lazyRoute.ts"
                );
                return manageEngagementScore;
              },
            },
            {
              path: "/manage-engagement-score/courses/:sessionId",
              loader: getInstructorCoursesLoader,
              shouldRevalidate: () => false,
            },
            {
              path: "/manage-engagement-score/:page/:vbid/:courseId",
              async lazy() {
                const { engagementScoreEmbed } = await import(
                  "./manage-engagement-score/lazyRoute.ts"
                );
                return engagementScoreEmbed;
              },
            },
            // Just an iframe, don't need to lazy load
            { path: "/order-history/:orderId?", element: <OrderHistory /> },
            {
              path: "/course-redirect/terms/:term/departments/:department/courses/:course/sections/:section",
              loader: courseRedirectLoader,
              element: <CourseRedirect />,
            },
            // Related to fraction route /#/activate/:vbid
            {
              path: "/activate/:vbid/*",
              loader: activateLoader,
              element: <AssetActivator />,
              handle: { sidebar: false } as HomeRouteHandle,
            },
            {
              // Legacy fraction route, /#/books/:vbid/.*
              path: "/books/:vbid?/*",
              loader: booksRouteLoader,
            },
            {
              // Legacy fraction route, /#/home/redeem/:code
              // Optional /test segment assists with testing outside the /home route overload
              path: "/test?/redeem/:code",
              loader: redeemLoader,
              element: <RedeemMain />,
            },
            {
              // Legacy fraction route, /#/home/subscribe/:email
              // Optional /test segment assists with testing outside the /home route overload
              path: "/test?/subscribe/:email",
              loader: subscribeLoader,
              element: <SubscribeMain />,
            },
            {
              path: "/free-books/:vbid",
              element: <FreeBookActivator />,
              handle: { sidebar: false } as HomeRouteHandle,
              children: [
                {
                  path: "/free-books/:vbid/activate",
                  action: activateFreeBookAction,
                },
              ],
            },
          ],
        },
      ],
    },
    /**
     * This section is for general actions that may be called on a variety of pages.
     * Page-specific actions should be co-located with the relevant page
     */
    {
      path: "/actions",
      children: [
        // VST Codes
        { path: "/actions/redeem", action: redeemCodesAction },
        // Third-party courseware
        { path: "/actions/reveal/:vbid", action: revealCode },
        // Language preference update
        { path: "/actions/user", action: updateUser },
        // User Preferences
        {
          path: "/actions/user/preferences/:id",
          action: updateUserPreference,
        },
        // QR Code
        {
          path: "/actions/user/dynamic-link",
          loader: getDynamicLink,
          shouldRevalidate: () => false,
        },
        // Explore book returns
        { path: "/actions/return/:vbid", action: returnAction },
        // Explore book borrows
        { path: "/actions/activate/:vbid", action: activateAction },
        // Favorites
        {
          path: "/actions/user/favorites",
          action: favorites,
        },
        {
          path: "/actions/update-library",
          action: updateLibraryAction,
        },
        // activate a promotion subscription (e.g. free books)
        {
          path: "/actions/promotion/activate/:vbid",
          action: promotionActivateAction,
        },
        // return a promotion subscription (e.g. free books)
        {
          path: "/actions/promotion/return/:vbid",
          action: promotionReturnAction,
        },
      ],
    },
    /**
     * This section is for general non-rendered fetches that may be called on a variety of pages.
     * Page-specific loads should be co-located with the relevant page
     */
    {
      path: "/data",
      children: [
        // Table of Contents
        {
          path: "/data/table-of-contents/:vbid",
          loader: tableOfContentsDataLoader,
          shouldRevalidate: () => false,
        },
        // More nuanced expired (e.g. recently expired)
        {
          path: "/data/expired/:page",
          loader: expiredDataLoader,
          // Used in sidebar, can't revalidate or it blocks navigation
          shouldRevalidate: () => false,
        },
        // Recommendations data for notification count
        {
          path: "/data/recommendations",
          loader: recommendationsDataLoader,
          // Used in sidebar, can't revalidate or it blocks navigation
          shouldRevalidate: () => false,
        },
      ],
    },
  ],
};
